import style from "./Button.module.scss";
import cn from "classnames";
import { RiLoader5Fill } from "react-icons/ri";
import { IoClose } from "react-icons/io5";
import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useEffect, useRef, useState } from "react";
import { ErrorModal, SuccessModal } from "./components";

export function Button({
  type = "button",
  text = "",
  icon,
  alignIcon = "right",
  form = "ellipse",
  color = "orange",
  variant = "filled",
  customStyle,
  onClick,
  loading = false,
  errors,
  resetStatus,
  successContent,
  disabled,
  tabIndex = 0
}: {
  type?: React.ButtonHTMLAttributes<HTMLButtonElement>["type"];
  text?: string;
  icon?: JSX.Element;
  alignIcon?: "left" | "right";
  form?: "rectangle" | "ellipse";
  color?: "orange" | "blue";
  variant?: "filled" | "outline"
  customStyle?: {
    width?: React.HTMLAttributes<HTMLElement>["className"];
    button?: React.HTMLAttributes<HTMLElement>["className"];
    text?: React.HTMLAttributes<HTMLElement>["className"];
    icon?: React.HTMLAttributes<HTMLElement>["className"];
  };
  onClick?: () => void,
  loading?: boolean,
  errors?: (
    undefined
    | string
    | ((func: typeof extractError) => string
      | {
        title: string,
        description: string | JSX.Element,
        button?: { text: string | JSX.Element, action: () => void }
      }))[],
  resetStatus?: () => void,
  successContent?: { title: string, description: string | JSX.Element, button?: { text: string | JSX.Element, action: () => void } },
  disabled?: boolean,
  tabIndex?: number
}) {
  const [errorListPosition, setErrorListPosition] = useState("top")

  const refButton = useRef<HTMLButtonElement | null>(null);
  const refErrorsList = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const buttonRect = refButton.current?.getBoundingClientRect();
    const errorsListHeight = refErrorsList.current?.offsetHeight || 0;

    const isIntersectionY = ((buttonRect?.bottom || 0) + errorsListHeight) > window.innerHeight;

    setErrorListPosition(isIntersectionY ? "bottom" : "top")
  }, [errors])

  const [unknownsError, knownsErrors] = (() => {
    const unknownsError: string[] = [];
    const knownsErrors: {
      title: string;
      description: string | JSX.Element;
      button?: { text: string | JSX.Element, action: () => void }
    }[] = [];

    errors?.forEach(error => {
      if (error) {
        if (typeof error === "string") {
          unknownsError.push(error)
        } else {
          const err = error(extractError);

          if (typeof err === "string") {
            unknownsError.push(err)
          } else if (err) {
            knownsErrors.push(err)
          }
        }
      }
    })

    return [unknownsError, knownsErrors]
  })()

  return (
    <div className={cn("relative w-fit", customStyle?.width)}>
      <button
        ref={refButton}
        type={type}
        className={cn(
          style.button,
          style["icon_" + alignIcon],
          style["form_" + form],
          style["color_" + color],
          style["variant_" + variant],
          customStyle?.button,
          loading || disabled && "opacity-70 cursor-not-allowed"
        )}
        onClick={onClick}
        disabled={loading || disabled}
        tabIndex={tabIndex}
      >
        <RiLoader5Fill className={cn(style.text, customStyle?.text, "animate-spin", loading ? 'block' : 'hidden')} />
        <div className={!loading ? 'flex justify-center gap-[5px] items-center' : 'hidden'}>
          {
            text
              ? <p className={cn(style.text, customStyle?.text)}>{unknownsError.length ? "Помилка" : text}</p>
              : null
          }
          {
            icon
              ? <div className={cn(style.icon, customStyle?.icon)}>{icon}</div>
              : null
          }
        </div>
      </button>
      <div
        ref={refErrorsList}
        style={{
          [errorListPosition]: "calc(100% + 10px)"
        }}
        className={cn(style.errorList, !unknownsError.length && "hidden")}
      >
        <IoClose
          className="ml-auto mb-[5px] w-[15px] h-[15px] cursor-pointer"
          onClick={resetStatus}
        />
        <div className="max-h-[130px] overflow-auto">
          {
            unknownsError?.map((error, i) => (
              <div key={i} className={style.error}>
                <div>{error}</div>
              </div>
            ))
          }
        </div>
      </div>
      {
        knownsErrors.map((error, i) => (
          <ErrorModal
            key={i}
            title={error.title}
            description={error.description}
            closeModal={resetStatus}
            button={error.button}
          />
        ))
      }
      {
        successContent
          ? <SuccessModal
            title={successContent.title}
            description={successContent.description}
            closeModal={resetStatus}
            button={successContent.button}
          />
          : null
      }
    </div>
  );
}

export function extractError(error: FetchBaseQueryError | SerializedError | undefined) {
  try {
    if (error) {
      const detail = (error as unknown as { data: { detail: string } }).data.detail;

      return typeof detail === "string" ? detail : "Невідома помилка";
    } else {
      return "Невідома помилка"
    }
  } catch (error) {
    return "Невідома помилка"
  }
}